import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TagModule } from 'primeng/tag';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { HttpClient } from '@angular/common/http';
import { GrowthSegmentSelectorComponent } from '../../shared/design-system/growth-segment-selector.component';
import { GrowthContentAssetSelectorComponent } from '../../shared/design-system/growth-content-asset-selector.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { GrowthAgentDefinition, GrowthAgentRun, GrowthAgentService } from './growth-agent.service';

@Component({
  standalone: true,
  selector: 'ain-growth-agent-console',
  imports: [
    FormsModule, RouterLink, AinPageComponent, AinStateComponent, InfoHintComponent,
    CardModule, ButtonModule, SelectModule, InputTextModule, TextareaModule, TagModule,
    GrowthSegmentSelectorComponent, GrowthContentAssetSelectorComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.agents.console.title')"
      [description]="t('growth.agents.console.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.agents.console.help.title'"
        [descriptionKey]="'growth.agents.console.help.description'"
        [bulletKeys]="[
          'growth.agents.console.help.bullets.advisory',
          'growth.agents.console.help.bullets.workflow',
          'growth.agents.console.help.bullets.noExecute'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" icon="pi pi-refresh" [label]="t('common.refresh')" (click)="load()"></button>
        <a routerLink="/growth/agents/recommendations" pButton severity="secondary" [label]="t('growth.agents.console.actions.recommendations')"></a>
        <a routerLink="/growth/agents/approval-queue" pButton severity="secondary" [label]="t('growth.agents.console.actions.approvalQueue')"></a>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('growth.agents.console.errorLoad')" [message]="error() ?? ''" />
      } @else {
        <section class="card">
          <h3>{{ t('growth.agents.console.sections.definitions') }}</h3>
          <div class="agent-grid">
            @for (agent of definitions(); track agent.id) {
              <p-card [header]="agent.name">
                <p-tag [value]="agent.agentKey" severity="secondary" />
                <p-tag [value]="agent.defaultAutomationLevel" severity="info" />
                <p class="meta">{{ agent.description }}</p>
                <button pButton type="button" size="small" [label]="t('growth.agents.console.actions.selectAgent')" (click)="selectAgent(agent)"></button>
              </p-card>
            }
          </div>
        </section>

        <section class="card">
          <h3>{{ t('growth.agents.console.sections.run') }}</h3>
          <div class="form-grid">
            <label>{{ t('growth.agents.console.fields.agent') }}
              <p-select [options]="agentOptions()" [(ngModel)]="runModel.agentKey" optionLabel="label" optionValue="value" [placeholder]="t('growth.agents.console.fields.agentPlaceholder')" appendTo="body" />
            </label>
            <label>{{ t('growth.agents.console.fields.industryKey') }}
              <p-select [options]="industryOptions" [(ngModel)]="runModel.industryKey" optionLabel="label" optionValue="value" appendTo="body" />
            </label>
            <label>{{ t('growth.agents.console.fields.segment') }}
              <ain-growth-segment-selector [(ngModel)]="runModel.segmentId" />
            </label>
            <label>{{ t('growth.agents.console.fields.contentAsset') }}
              <ain-growth-content-asset-selector [(ngModel)]="runModel.contentAssetId" />
            </label>
            <label>{{ t('growth.agents.console.fields.campaign') }}
              <p-select [options]="campaignOptions()" [(ngModel)]="runModel.campaignId" optionLabel="label" optionValue="value" [showClear]="true" [placeholder]="t('growth.agents.console.fields.campaignPlaceholder')" appendTo="body" />
            </label>
            <label class="full">{{ t('growth.agents.console.fields.scopeNotes') }}
              <textarea pTextarea rows="2" [(ngModel)]="runModel.scopeNotes"></textarea>
            </label>
          </div>
          <div class="actions">
            <button pButton type="button" [label]="t('growth.agents.console.actions.run')" icon="pi pi-play" [loading]="running()" (click)="runAgent()"></button>
          </div>
          @if (lastRun()) {
            <div class="run-result">
              <p><strong>{{ t('growth.agents.console.fields.status') }}:</strong> {{ lastRun()!.status }}</p>
              <p><strong>{{ t('growth.agents.console.fields.providerMode') }}:</strong> {{ lastRun()!.providerMode }}</p>
              <p>{{ lastRun()!.outputSummary }}</p>
              <a [routerLink]="['/growth/agents/runs', lastRun()!.id]">{{ t('growth.agents.console.actions.viewRun') }}</a>
            </div>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .agent-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; }
    .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 10px; }
    .form-grid label { display: grid; gap: 4px; font-size: 0.875rem; }
    .form-grid .full { grid-column: 1 / -1; }
    .actions { display: flex; gap: 8px; }
    .meta { color: #6b7280; font-size: 0.875rem; }
    .run-result { background: #f9fafb; border-radius: 8px; padding: 10px; }
  `]
})
export class GrowthAgentConsoleComponent implements OnInit {
  private readonly service = inject(GrowthAgentService);
  private readonly http = inject(HttpClient);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(false);
  readonly running = signal(false);
  readonly error = signal<string | null>(null);
  readonly definitions = signal<GrowthAgentDefinition[]>([]);
  readonly lastRun = signal<GrowthAgentRun | null>(null);

  runModel = {
    agentKey: 'growth',
    segmentId: null as string | null,
    contentAssetId: null as string | null,
    campaignId: null as string | null,
    industryKey: 'retail',
    scopeNotes: ''
  };

  readonly industryOptions = [
    { label: 'Retail', value: 'retail' },
    { label: 'Banking', value: 'banking' }
  ];

  t(key: string): string { return this.i18n.translate(key); }

  agentOptions = signal<{ label: string; value: string }[]>([]);
  campaignOptions = signal<{ label: string; value: string }[]>([]);

  ngOnInit(): void {
    this.load();
    this.loadCampaigns();
  }

  loadCampaigns(): void {
    this.http.get<Array<{ id: string; name: string }>>('/api/v1/growth/marketing/campaigns').subscribe({
      next: items => this.campaignOptions.set(items.map(c => ({ label: c.name, value: c.id }))),
      error: () => this.campaignOptions.set([])
    });
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.service.listDefinitions().subscribe({
      next: items => {
        this.definitions.set(items);
        this.agentOptions.set(items.map(a => ({ label: a.name, value: a.agentKey })));
        this.loading.set(false);
      },
      error: err => {
        this.error.set(err?.error?.error ?? err?.message ?? 'Error');
        this.loading.set(false);
      }
    });
  }

  selectAgent(agent: GrowthAgentDefinition): void {
    this.runModel.agentKey = agent.agentKey;
  }

  runAgent(): void {
    this.running.set(true);
    this.service.runAgent(this.runModel.agentKey, {
      segmentId: this.runModel.segmentId,
      contentAssetId: this.runModel.contentAssetId,
      campaignId: this.runModel.campaignId,
      industryKey: this.runModel.industryKey,
      scopeNotes: this.runModel.scopeNotes || null,
      idempotencyKey: `agent-run-${Date.now()}`,
      correlationId: `console-${this.runModel.agentKey}`
    }).subscribe({
      next: res => {
        this.lastRun.set(res.run);
        this.running.set(false);
      },
      error: err => {
        this.error.set(err?.error?.error ?? err?.message ?? 'Error');
        this.running.set(false);
      }
    });
  }
}
